import React, { useState } from 'react';
import { X, Copy, Check, Server, Globe, Code, Link as LinkIcon } from 'lucide-react';
import { Domain } from '../types';

interface EmbedModalProps {
  domain: Domain;
  onClose: () => void;
}

export const EmbedModal: React.FC<EmbedModalProps> = ({ domain, onClose }) => {
  const [copiedFrontend, setCopiedFrontend] = useState(false);
  
  const currentOrigin = window.location.origin;
  const trackerUrl = `${currentOrigin}/tracker.js`;

  const frontendCode = `<!-- MyETRAFFIC Tracking Code -->
<script 
  src="${trackerUrl}" 
  data-id="${domain.id}" 
  async>
</script>`;

  const copyToClipboard = (text: string, setFn: (b: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setFn(true);
    setTimeout(() => setFn(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] border border-slate-200 dark:border-slate-800 overflow-hidden">
        
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-lg text-indigo-600 dark:text-indigo-400">
              <Globe className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white truncate">Setup Guide</h2>
              <p className="text-[10px] md:text-sm text-slate-500 truncate">Tracking code for {domain.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <X size={20} className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 md:p-8 bg-slate-50 dark:bg-slate-950">
          <div className="max-w-3xl mx-auto space-y-6 md:space-y-8">

            <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden border-l-4 border-l-brand-500">
              <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex justify-between items-center">
                 <div className="flex items-center gap-2">
                    <Code className="text-brand-500" size={16} />
                    <h3 className="font-bold text-slate-900 dark:text-white text-sm md:text-base">1. Tracking Script</h3>
                 </div>
                 <span className="text-[9px] font-mono text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 px-1.5 py-0.5 rounded border border-brand-200">Auto-Update</span>
              </div>
              <div className="p-5 md:p-6">
                 <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 mb-4">
                   Copy and paste this snippet into the <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">head</code> or <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">body</code>.
                 </p>
                <div className="relative group">
                  <pre className="p-4 md:p-5 bg-slate-900 text-indigo-100 font-mono text-[10px] md:text-xs rounded-xl overflow-x-auto leading-relaxed scrollbar-thin scrollbar-thumb-slate-700">
                    {frontendCode}
                  </pre>
                  <button 
                    onClick={() => copyToClipboard(frontendCode, setCopiedFrontend)}
                    className="absolute top-2 right-2 md:top-3 md:right-3 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-2 transition-all shadow-md"
                  >
                    {copiedFrontend ? <Check size={12} /> : <Copy size={12} />}
                    {copiedFrontend ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="mt-4 flex items-start gap-3 text-[10px] md:text-xs text-slate-500 dark:text-slate-400 bg-indigo-50 dark:bg-indigo-900/10 p-3 rounded-lg border border-indigo-100">
                  <div className="mt-0.5 text-indigo-500 shrink-0"><Server size={14} /></div>
                  <p>Updates are applied automatically without you needing to change this code again.</p>
                </div>
              </div>
            </section>

             <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
               <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2">
                     <LinkIcon className="text-purple-500" size={16} />
                     <h3 className="font-bold text-slate-900 dark:text-white text-sm md:text-base">2. Campaign Links</h3>
                  </div>
               </div>
               <div className="p-5 md:p-6">
                  <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 mb-3">Detect sources via the <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-bold">?s=</code> parameter.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[10px] font-mono">
                    <div className="bg-slate-50 dark:bg-slate-800 p-2.5 rounded border border-slate-200 flex justify-between items-center">
                      <span className="text-slate-500">Facebook Ad</span>
                      <span className="text-purple-600 font-bold">?s=fb_ad</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 p-2.5 rounded border border-slate-200 flex justify-between items-center">
                      <span className="text-slate-500">Newsletter</span>
                      <span className="text-purple-600 font-bold">?s=email</span>
                    </div>
                  </div>
               </div>
             </section>

          </div>
        </div>

        <div className="p-5 md:p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg"
          >
            Finished Setup
          </button>
        </div>
      </div>
    </div>
  );
};